import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useState} from 'react';

import {FormState} from 'analysis-tool/data-frame-creation/schemas/RegistrySchemaDataFrameCreationTypes';
import RegistrySchemasStepperForm from 'analysis-tool/data-frame-creation/schemas/RegistrySchemasStepperForm';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import {setValueInConfig} from 'pipelines/generalized-step-config-editor/step-config-settings/StepConfigSettingsUtils';
import {CustomComponentEditorProps} from 'pipelines/generalized-step-config-editor/StepConfigEditorTypes';
import useQueryApiIntrospection from 'query-api/useQueryApiIntrospection';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {
  DEFAULT_QUERY_FORM_STATE,
  buildQueryStringFromFormState,
  notifyIfFieldCountExceeded,
} from './LookUpQueryUtils';
import {useDataFrameNameStateBridge} from './useDataFrameNameStateBridge';

export default function RegistrySchemaQueryEditor({
  config,
  onChangeConfig,
}: CustomComponentEditorProps): JSX.Element {
  const [queryFormState, setQueryFormState] = useState<FormState>(DEFAULT_QUERY_FORM_STATE);
  const queryApiIntrospection = useQueryApiIntrospection();

  const handleSetQueryFormState = useCallback(
    (newFormState: FormState) => {
      if (notifyIfFieldCountExceeded(newFormState)) {
        return;
      }
      setQueryFormState(newFormState);
      const queryString = buildQueryStringFromFormState(newFormState);
      if (queryString == null) {
        return;
      }
      const updatedConfig = setValueInConfig(config, ['query'], queryString);
      onChangeConfig(updatedConfig, ['queryDescription'], '');
    },
    [config, onChangeConfig]
  );

  const {dataFrameNameState, handleSetDataFrameNameState} = useDataFrameNameStateBridge(
    config,
    onChangeConfig
  );

  if (queryApiIntrospection.status === QueryResultType.ERROR) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text="Error loading registry schemas." />;
  }
  if (queryApiIntrospection.status === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  }

  return (
    <div className={css(styles.wrapper)}>
      <RegistrySchemasStepperForm
        queryApiIntrospection={queryApiIntrospection}
        queryFormState={queryFormState}
        setQueryFormState={handleSetQueryFormState}
        setIsManuallyChanged={() => {}}
        dataFrameNameState={dataFrameNameState}
        setDataFrameNameState={handleSetDataFrameNameState}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    paddingBottom: '5px',
  },
});
